import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Alert from 'flarum/common/components/Alert';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import Modal from 'flarum/common/components/Modal';
import Composer from 'flarum/forum/components/Composer';
import ForumApplication from 'flarum/forum/ForumApplication';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import IndexPage from 'flarum/forum/components/IndexPage';
import ForgotPasswordModal from 'flarum/forum/components/ForgotPasswordModal';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';

class DrawerAuthLogInModal extends LogInModal {
  view() {
    return (
      <div className={`Modal modal-dialog fade ${this.className()}`}>
        <div className="Modal-content">
          <div className="Modal-close DrawerAuthModal-close">
            <Button
              icon="fas fa-times"
              onclick={() => this.hide()}
              className="Button Button--icon Button--link"
              aria-label={app.translator.trans('core.lib.modal.close')}
            />
          </div>

          <form onsubmit={this.onsubmit.bind(this)}>
            <div className="Modal-header">
              <h3 className="App-titleControl App-titleControl--text">{this.title()}</h3>
            </div>

            {!!this.alertAttrs && (
              <div className="Modal-alert">
                <Alert {...this.alertAttrs} />
              </div>
            )}

            {this.content()}
          </form>
        </div>
      </div>
    );
  }

  forgotPassword() {
    const email = this.identification();
    const attrs = email.includes('@') ? { email } : undefined;

    app.modal.show(ForgotPasswordModal, attrs);
  }

  signUp() {
    const identification = this.identification();

    app.modal.show(DrawerAuthSignUpModal, {
      [identification.includes('@') ? 'email' : 'username']: identification,
    });
  }
}

class DrawerAuthSignUpModal extends SignUpModal {
  view() {
    return (
      <div className={`Modal modal-dialog fade ${this.className()}`}>
        <div className="Modal-content">
          <div className="Modal-close DrawerAuthModal-close">
            <Button
              icon="fas fa-times"
              onclick={() => this.hide()}
              className="Button Button--icon Button--link"
              aria-label={app.translator.trans('core.lib.modal.close')}
            />
          </div>

          <form onsubmit={this.onsubmit.bind(this)}>
            <div className="Modal-header">
              <h3 className="App-titleControl App-titleControl--text">{this.title()}</h3>
            </div>

            {!!this.alertAttrs && (
              <div className="Modal-alert">
                <Alert {...this.alertAttrs} />
              </div>
            )}

            {this.content()}
          </form>
        </div>
      </div>
    );
  }

  logIn() {
    app.modal.show(DrawerAuthLogInModal, {
      identification: this.email() || this.username(),
    });
  }
}

function clearMobileComposerStyles(composer) {
  composer.$().css({
    position: '',
    top: '',
    left: '',
    right: '',
    bottom: '',
    width: '',
    maxWidth: '',
    height: '',
    maxHeight: '',
    opacity: '',
    transform: '',
  });
}

function applyMobileMinimizedComposerStyles(composer) {
  composer.$().css({
    position: 'fixed',
    top: 'auto',
    left: 0,
    right: 0,
    bottom: 0,
    width: 'auto',
    maxWidth: 'none',
    height: '',
    maxHeight: '',
    opacity: 1,
    transform: 'none',
  });
}

function cancelMobileComposerAnimation(composer) {
  if (composer.drawerComposerAnimation) {
    composer.drawerComposerAnimation.cancel();
    composer.drawerComposerAnimation = null;
  }
}

function removeMobileComposerMinimizeClone(composer) {
  if (composer.drawerComposerMinimizeClone) {
    composer.drawerComposerMinimizeClone.remove();
    composer.drawerComposerMinimizeClone = null;
  }
}

function visibleSidebarTagCount() {
  const raw = parseInt(app.forum.attribute('porntiersSidebarVisibleTags') || '8', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 8;
}

function applyConfiguredThemeVariables() {
  const colorScheme = document.querySelector('meta[name="color-scheme"]')?.content || '';
  const isDark = colorScheme.includes('dark');

  const variables = {
    '--pink': app.forum.attribute('porntiersThemePink'),
    '--rose': app.forum.attribute('porntiersThemeRose'),
    '--violet': app.forum.attribute('porntiersThemeViolet'),
    '--bg-main-start': app.forum.attribute(isDark ? 'porntiersThemeDarkBgMainStart' : 'porntiersThemeLightBgMainStart'),
    '--bg-main-end': app.forum.attribute(isDark ? 'porntiersThemeDarkBgMainEnd' : 'porntiersThemeLightBgMainEnd'),
    '--panel-bg': app.forum.attribute(isDark ? 'porntiersThemeDarkPanelBg' : 'porntiersThemeLightPanelBg'),
    '--header-bg': app.forum.attribute(isDark ? 'porntiersThemeDarkHeaderBg' : 'porntiersThemeLightHeaderBg'),
    '--text-1': app.forum.attribute(isDark ? 'porntiersThemeDarkText1' : 'porntiersThemeLightText1'),
    '--text-2': app.forum.attribute(isDark ? 'porntiersThemeDarkText2' : 'porntiersThemeLightText2'),
  };

  Object.entries(variables).forEach(([name, value]) => {
    if (value) {
      document.documentElement.style.setProperty(name, value);
    }
  });
}

function getFooterLinks() {
  const fallbackLinks = [
    { label: 'Home', url: app.route('index') },
    { label: 'Latest', url: app.route('index', { sort: 'latest' }) },
    { label: 'Top', url: app.route('index', { sort: 'top' }) },
  ];

  const value = app.forum.attribute('porntiersFooterLinks');

  if (!value) return fallbackLinks;

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) return fallbackLinks;

    const links = parsed.filter((item) => item && item.label && item.url).map((item) => ({ label: item.label, url: item.url }));

    return links.length ? links : fallbackLinks;
  } catch {
    return fallbackLinks;
  }
}

const ForumFooter = {
  view() {
    const forumTitle = app.forum.attribute('title');
    const year = new Date().getFullYear();
    const brand = app.forum.attribute('porntiersFooterBrand') || forumTitle;
    const tagline = app.forum.attribute('porntiersFooterTagline') || 'Community conversations, curated cleanly.';
    const copyrightTemplate = app.forum.attribute('porntiersFooterCopyright') || '© {year} {forum}';
    const copyright = copyrightTemplate.split('{year}').join(String(year)).split('{forum}').join(forumTitle);
    const links = getFooterLinks();

    return (
      <footer className="PorntiersForumFooter">
        <div className="container">
          <div className="PorntiersForumFooter-inner">
            <div className="PorntiersForumFooter-brand">
              <strong>{brand}</strong>
              <span>{tagline}</span>
            </div>

            <nav className="PorntiersForumFooter-links" aria-label="Footer">
              {links.map((link) => (
                <a href={link.url}>{link.label}</a>
              ))}
            </nav>

            <div className="PorntiersForumFooter-meta">{copyright}</div>
          </div>
        </div>
      </footer>
    );
  },
};

app.initializers.add('porntiers-forum-ui', () => {
  extend(ForumApplication.prototype, 'mount', function () {
    applyConfiguredThemeVariables();

    document.addEventListener('fofnightmodechange', applyConfiguredThemeVariables);

    const appEl = document.getElementById('app');

    if (appEl && !document.getElementById('porntiers-forum-footer')) {
      const footerMount = document.createElement('div');
      footerMount.id = 'porntiers-forum-footer';
      appEl.appendChild(footerMount);
      m.mount(footerMount, ForumFooter);
    }
  });

  override(Modal.prototype, 'view', function (original) {
    const className = this.className?.();

    if (!className || !className.includes('PollDiscussionModal')) {
      return original();
    }

    if (this.alertAttrs) {
      this.alertAttrs.dismissible = false;
    }

    return (
      <div className={`Modal modal-dialog fade ${className}`}>
        <div className="Modal-content">
          {this.dismissibleOptions.viaCloseButton && (
            <div className="Modal-close PollDiscussionModal-close">
              <Button
                icon="fas fa-times"
                onclick={() => this.hide()}
                className="Button Button--icon Button--link"
                aria-label={app.translator.trans('core.lib.modal.close')}
              />
            </div>
          )}

          <form onsubmit={this.onsubmit.bind(this)}>
            <div className="Modal-header">
              <h3 className="App-titleControl App-titleControl--text">{this.title()}</h3>
            </div>

            {!!this.alertAttrs && (
              <div className="Modal-alert">
                <Alert {...this.alertAttrs} />
              </div>
            )}

            {this.content()}
          </form>
        </div>
      </div>
    );
  });

  override(IndexPage.prototype, 'hero', function (original) {
    if (typeof this.currentTag === 'function' && this.currentTag()) {
      return null;
    }

    return original();
  });

  extend(IndexPage.prototype, 'viewItems', function (items) {
    if (typeof this.currentTag !== 'function') return;

    const tag = this.currentTag();
    if (!tag) return;

    items.remove('sort');

    items.add(
      'tagSummary',
      <li className="PorntiersTagSummary">
        <span className="PorntiersTagSummary-name">{tag.name()}</span>
        {!!tag.description() && <span className="PorntiersTagSummary-description">{tag.description()}</span>}
      </li>,
      90
    );
  });

  extend(IndexPage.prototype, 'navItems', function (items) {
    const tagItems = Object.values(items.toObject())
      .filter((item) => item.itemName.startsWith('tag'))
      .sort((a, b) => b.priority - a.priority);

    if (tagItems.length <= visibleSidebarTagCount()) {
      return;
    }

    const currentTag = typeof this.currentTag === 'function' ? this.currentTag() : null;
    const currentTagId = currentTag ? String(currentTag.id()) : null;
    const currentParentId = currentTag?.parent() ? String(currentTag.parent().id()) : null;

    let visible = 0;

    tagItems.forEach((item) => {
      const tagId = item.itemName.replace('tag', '');
      const isCurrent = tagId === currentTagId || tagId === currentParentId;

      if (visible < visibleSidebarTagCount() || isCurrent) {
        visible++;
        return;
      }

      items.remove(item.itemName);
    });

    items.remove('moreTags');
    items.add(
      'moreTags',
      <LinkButton href={app.route('tags')} className="PorntiersTagsMoreLink">
        {app.translator.trans('flarum-tags.forum.index.more_link')}
      </LinkButton>,
      -16
    );
  });

  extend(IndexPage.prototype, 'actionItems', function (items) {
    if (typeof this.currentTag !== 'function') return;

    const tag = this.currentTag();
    if (!tag) return;

    const sortMap = app.discussions.sortMap();
    const sortOptions = Object.keys(sortMap).reduce((acc, sortId) => {
      acc[sortId] = app.translator.trans(`core.forum.index_sort.${sortId}_button`);
      return acc;
    }, {});

    items.add(
      'sort',
      <Dropdown
        buttonClassName="Button"
        label={sortOptions[app.search.params().sort] || Object.keys(sortMap).map((key) => sortOptions[key])[0]}
        accessibleToggleLabel={app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label')}
      >
        {Object.keys(sortOptions).map((value) => {
          const label = sortOptions[value];
          const active = (app.search.params().sort || Object.keys(sortMap)[0]) === value;

          return (
            <Button icon={active ? 'fas fa-check' : true} onclick={app.search.changeSort.bind(app.search, value)} active={active}>
              {label}
            </Button>
          );
        })}
      </Dropdown>,
      110
    );

  });

  extend(Composer.prototype, 'oncreate', function () {
    const toolbarButtonSelector = '.TextEditor-toolbar button, .TextEditor-toolbar .Button, .TextEditor-controls button, .TextEditor-controls .Button';

    this.drawerToolbarBlurHandler = (event) => {
      if (app.screen() !== 'phone') return;

      const button = event.target.closest(toolbarButtonSelector);

      if (!button) return;

      setTimeout(() => button.blur(), 0);
    };

    this.drawerToolbarFocusHandler = (event) => {
      if (app.screen() !== 'phone') return;

      const button = event.target.closest(toolbarButtonSelector);

      if (!button) return;

      button.blur();
    };

    this.$().on('click', this.drawerToolbarBlurHandler);
    this.$().on('focusin', this.drawerToolbarFocusHandler);
  });

  extend(Composer.prototype, 'onremove', function () {
    removeMobileComposerMinimizeClone(this);

    if (this.drawerToolbarBlurHandler) {
      this.$().off('click', this.drawerToolbarBlurHandler);
      this.drawerToolbarBlurHandler = null;
    }

    if (this.drawerToolbarFocusHandler) {
      this.$().off('focusin', this.drawerToolbarFocusHandler);
      this.drawerToolbarFocusHandler = null;
    }
  });

  override(Composer.prototype, 'show', function (original) {
    if (app.screen() !== 'phone') return original();

    const $composer = this.$().stop(true, true);
    const composerEl = $composer[0];

    if (this.drawerComposerHideTimer) {
      clearTimeout(this.drawerComposerHideTimer);
      this.drawerComposerHideTimer = null;
    }

    cancelMobileComposerAnimation(this);
    removeMobileComposerMinimizeClone(this);

    this.updateHeight();
    $composer
      .show()
      .css({
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        opacity: 1,
        transform: 'translate(-50%, -50%) scale(1)',
      });

    this.showBackdrop();

    if (composerEl?.animate) {
      this.drawerComposerAnimation = composerEl.animate(
        [
          { opacity: 0, transform: 'translate(-50%, -46%) scale(0.96)' },
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
        ],
        { duration: 200, easing: 'ease-out', fill: 'both' }
      );

      this.drawerComposerAnimation.onfinish = () => {
        this.drawerComposerAnimation = null;
        $composer.css({ opacity: 1, transform: 'translate(-50%, -50%) scale(1)' });
      };
    }

    setTimeout(() => this.focus(), 200);
  });

  override(Composer.prototype, 'hide', function (original) {
    if (app.screen() !== 'phone') return original();

    const $composer = this.$().stop(true, true);
    const composerEl = $composer[0];

    if (this.drawerComposerHideTimer) {
      clearTimeout(this.drawerComposerHideTimer);
    }

    cancelMobileComposerAnimation(this);
    removeMobileComposerMinimizeClone(this);

    if (this.prevPosition === 'minimized' || $composer.hasClass('minimized')) {
      applyMobileMinimizedComposerStyles(this);

      $composer.hide();
      clearMobileComposerStyles(this);
      this.hideBackdrop();
      this.updateBodyPadding();

      return;
    }

    if (composerEl?.animate) {
      this.drawerComposerAnimation = composerEl.animate(
        [
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          { opacity: 0, transform: 'translate(-50%, -47%) scale(0.98)' },
        ],
        { duration: 200, easing: 'ease-out', fill: 'both' }
      );
    } else {
      $composer.css({
        opacity: 0,
        transform: 'translate(-50%, -47%) scale(0.98)',
      });
    }

    this.drawerComposerHideTimer = setTimeout(() => {
      cancelMobileComposerAnimation(this);
      $composer.hide();
      clearMobileComposerStyles(this);
      this.hideBackdrop();
      this.updateBodyPadding();
      this.drawerComposerHideTimer = null;
    }, 200);
  });

  override(Composer.prototype, 'minimize', function (original) {
    if (app.screen() !== 'phone') return original();

    const $composer = this.$().stop(true, true);
    const composerEl = $composer[0];

    if (this.drawerComposerHideTimer) {
      clearTimeout(this.drawerComposerHideTimer);
      this.drawerComposerHideTimer = null;
    }

    cancelMobileComposerAnimation(this);
    removeMobileComposerMinimizeClone(this);

    if (!composerEl) return original();

    const startRect = composerEl.getBoundingClientRect();

    clearMobileComposerStyles(this);
    original();

    requestAnimationFrame(() => {
      const endEl = $composer[0];

      if (!endEl || !startRect.width || !startRect.height) {
        return;
      }

      const endRect = endEl.getBoundingClientRect();

      if (!endRect.width || !endRect.height) {
        return;
      }

      const clone = composerEl.cloneNode(true);
      clone.style.position = 'fixed';
      clone.style.top = `${startRect.top}px`;
      clone.style.left = `${startRect.left}px`;
      clone.style.width = `${startRect.width}px`;
      clone.style.height = `${startRect.height}px`;
      clone.style.margin = '0';
      clone.style.maxWidth = 'none';
      clone.style.maxHeight = 'none';
      clone.style.pointerEvents = 'none';
      clone.style.zIndex = '10001';
      clone.style.overflow = 'hidden';
      clone.style.willChange = 'top, left, width, height, opacity, border-radius';

      document.body.appendChild(clone);
      this.drawerComposerMinimizeClone = clone;
      endEl.style.visibility = 'hidden';

      cancelMobileComposerAnimation(this);
      this.drawerComposerAnimation = clone.animate(
        [
          {
            top: `${startRect.top}px`,
            left: `${startRect.left}px`,
            width: `${startRect.width}px`,
            height: `${startRect.height}px`,
            opacity: 1,
            borderRadius: '18px',
          },
          {
            top: `${endRect.top}px`,
            left: `${endRect.left}px`,
            width: `${endRect.width}px`,
            height: `${endRect.height}px`,
            opacity: 0.98,
            borderRadius: '0px',
          },
        ],
        { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
      );

      this.drawerComposerAnimation.onfinish = () => {
        this.drawerComposerAnimation = null;
        removeMobileComposerMinimizeClone(this);
        endEl.style.visibility = '';
      };
    });
  });

  extend(HeaderSecondary.prototype, 'items', function (items) {
    if (items.has('signUp')) {
      items.setContent(
        'signUp',
        <Button
          className="Button Button--link"
          onclick={() => {
            app.drawer.hide();
            app.modal.show(DrawerAuthSignUpModal);
          }}
        >
          {app.translator.trans('core.forum.header.sign_up_link')}
        </Button>
      );
    }

    if (items.has('logIn')) {
      items.setContent(
        'logIn',
        <Button
          className="Button Button--link"
          onclick={() => {
            app.drawer.hide();
            app.modal.show(DrawerAuthLogInModal);
          }}
        >
          {app.translator.trans('core.forum.header.log_in_link')}
        </Button>
      );
    }
  });
});
